import React from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
} from "react-native";
import { ProgressCircle } from "react-native-svg-charts";

import moment from "moment";

import Utils, { durationToHHmm } from "../../../../helpers/utils";
import ImageComponent from "../../common/ImageComponent";
import GoalHours from "./GoalHours";
import { cardShadow } from "../../../../styles/shadows";
import AppTheme from "../../AppTheme";
import fontStyles from "../../../fonts";
import { getGoalProgressColor } from "../../common/mood/MoodsHelper";

export default function Goal({
  controller,
  goal,
  hasGem,
  style = {},
  allowEdit = true,
}) {
  const hours = durationToHHmm(goal.hours);
  const target_hours = durationToHHmm(goal.target_hours);
  const frequency = goal.time_unit == "day" ? "daily" : `${goal.time_unit}ly`;

  let timeUnit = "";
  switch (goal.time_unit) {
    case "week":
      timeUnit = "wk";
      break;
    case "month":
      timeUnit = "mth";
      break;
    case "quarter":
      timeUnit = "qtr";
      break;
    case "year":
      timeUnit = "yr";
      break;
    default:
      timeUnit = "dy";
      break;
  }

  const { text } = goal;
  let progressColor = getGoalProgressColor(goal);

  const now = Utils.nowUserTz(controller);
  if (goal.from != null && now.isBefore(moment(goal.from))) {
    progressColor = "#F0F0F0";
  }

  const progress =
    hours?.hours == 0 && hours?.minutes == 0
      ? 1
      : Math.max(Math.min(Math.abs(goal.total_pct), 1), 0);

  const editGoal = () => {
    if (allowEdit) {
      controller.changePage("feed", {
        selectedPage: "rn:resolution_detail",
        args: {
          goal,
        },
      });
    }
  };

  const formatDate = (date = "") => {
    if (!date) return "";
    return moment(date).format("MMM DD");
  };

  return (
    <TouchableOpacity style={[styles.card, style]} onPress={editGoal}>
      {goal.target_achieved == 1 ? (
        <ImageComponent
          styles={styles.confetti}
          image={require("../../../../assets/goals/confetti.png")}
        />
      ) : null}
      <View style={styles.content}>
        {/* Info */}
        <View style={styles.cardInfo}>
          <View style={styles.infoHeader}>
            <ImageComponent
              icon={goal.target_emoji}
              styles={styles.icon}
              onPress={editGoal}
            />
            <View style={{ marginBottom: 6 }}>
              <Text style={styles.headerText} numberOfLines={1}>
                {goal.target_name}
              </Text>
              <Text
                style={[
                  fontStyles.captionThin,
                  { textTransform: "capitalize" },
                ]}
                numberOfLines={1}
              >
                {frequency}
              </Text>
            </View>
          </View>

          {hasGem ? (
            <View style={styles.datesContainer}>
              {goal.from ? (
                <View>
                  <Text style={styles.dateLabel}>
                    Start{moment(goal.from).isAfter(now) ? "ing" : "ed"}
                  </Text>
                  <Text style={styles.date}>{formatDate(goal.from)}</Text>
                </View>
              ) : null}

              {goal.to ? (
                <View>
                  {goal.target_achieved ? (
                    <Text style={styles.dateLabel}>Achieved</Text>
                  ) : (
                    <Text style={styles.dateLabel}>Finished</Text>
                  )}
                  <Text style={styles.date}>{formatDate(goal.to)}</Text>
                </View>
              ) : null}
            </View>
          ) : (
            <Text style={styles.infoText} numberOfLines={2}>
              {text}
            </Text>
          )}
        </View>

        {/* Chart */}
        <View style={styles.progressContainer}>
          <ProgressCircle
            strokeWidth={8}
            style={styles.progressChart}
            progress={progress}
            progressColor={progressColor}
          />
          {/* Numbers */}
          <View style={styles.progressNumbers}>
            {hours ? <GoalHours hours={hours} /> : null}
            <View style={[styles.separator]} />
            {target_hours ? <GoalHours hours={target_hours} /> : null}
          </View>
        </View>
      </View>
      <Text style={styles.timeUnit}>{timeUnit}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    position: "relative",
    backgroundColor: "#FFF",
    borderRadius: 4,
    marginBottom: 8,
    paddingHorizontal: 8,
    marginHorizontal: 2,
    height: 86,
    ...cardShadow,
    elevation: 3,
  },
  content: {
    paddingRight: 24,
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    height: "100%",
  },
  cardInfo: {
    paddingTop: 5,
    display: "flex",
    alignItems: "flex-start",
    flex: 1,
    height: "100%",
  },
  infoHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  icon: {
    width: 24,
    height: 24,
    marginRight: 6,
  },
  headerText: {
    paddingTop: 10,
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  infoText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: AppTheme.almostBlack,
  },
  progressContainer: {
    position: "relative",
    width: 70,
    height: 70,
  },
  progressChart: {
    width: "100%",
    height: "100%",
  },
  progressNumbers: {
    position: "absolute",
    height: 70,
    width: 70,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  separator: {
    borderBottomColor: "rgba(36, 50, 81, 0.6)",
    borderBottomWidth: 1,
    width: 20,
    height: 1,
  },
  timeUnit: {
    position: "absolute",
    right: 8,
    bottom: 8,
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  confetti: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },
  datesContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    minWidth: 100,
    position: "absolute",
    left: 16,
    bottom: 10,
  },
  dateLabel: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
  date: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
  },
});
